import { useAuth } from "@clerk/clerk-react";
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Email,
  GmailThread,
  OutreachContactAutomatedStatus,
} from "schemas/dashboard";
import {
  EmailAddress,
  EmailDraft,
  EmailThreadSetting,
  Message,
} from "schemas/email";
import { SetAlertType } from "schemas/functions";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import Drawer from "components/Drawer";
import EmailThread from "components/EmailThread";
import EmailDraftComponent from "components/EmailThread/EmailDraft";
import { hasInvalidEmail } from "components/EmailThread/EmailDraft/utils";
import PendingEmailsDraft from "components/EmailThread/PendingEmail";
import { getPageName } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { navigateBackPath } from "utils/navigation";
import { isInvalidEmail } from "utils/string";
import { trackEvent } from "utils/tracking";

import { useOutreachTable } from "../../useOutreachTable";
import styles from "./styles";

interface OutreachEmailDrawerProps {
  setAlert: SetAlertType;
}

export default function OutreachEmail({ setAlert }: OutreachEmailDrawerProps) {
  const { openAttachment } = useOutreachTable();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { outreachContactId: id, threadId } = useParams();
  const outreachContactId = Number(id);

  const { currentOrg, currentUser } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const {
    emailHealth,
    setOpenIntegrationDialog,
    fetchIntegrationHealth,
    userIntegration,
  } = useContext(UserIntegrationsContext);
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateActionItems } = useContext(ActionItemsContext);

  const displayFollowUp =
    searchParams.get("display_followup_templates") === "true";

  const [showDraftEditor, setShowDraftEditor] = useState<boolean>(false);
  const [gmailThread, setGmailThread] = useState<GmailThread | undefined>();
  const [emailDraft, setEmailDraft] = useState<EmailDraft>({
    toContacts: [],
    ccContacts: [],
    body: "",
    subject: "",
  });
  const [emailThreadSetting, setEmailThreadSetting] =
    useState<EmailThreadSetting>({
      disableFollowUpTasks: false,
    });
  const [pendingEmails, setPendingEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const { currentContact, fetchContact, fetchEmailThreads } = useContext(
    OutreachContactsContext,
  );
  const [missingIntegration, setMissingIntegration] = useState(false);

  const handleBack = () => {
    if (emailDraft?.isChanged) {
      setConfirmDialogOpen(true);
      return;
    }
    navigate(navigateBackPath(location.pathname, 2));
  };

  const closeDrawer = () => {
    navigate(`/${routes.inbox}`);
  };

  const fetchEmailThread = async () => {
    if (!currentOrg || currentContact?.id !== outreachContactId) {
      return;
    }
    setLoading(true);
    setGmailThread(undefined);
    setEmailThreadSetting({
      disableFollowUpTasks: false,
    });
    setPendingEmails([]);
    if (!emailDraft?.body) {
      setEmailDraft({
        toContacts: [],
        ccContacts: [],
        body: "",
        subject: "",
      });
    }

    try {
      const pageName = getPageName();
      const threadViewParams = pageName
        ? `?source=${encodeURIComponent(pageName)}`
        : "";
      const { gmailThread, pendingEmails, emailDraft, emailThreadSetting } =
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/outreach-contacts/${outreachContactId}/threads/${threadId}${threadViewParams}`,
          "GET",
        );
      setGmailThread(gmailThread);
      setEmailThreadSetting(emailThreadSetting);
      setPendingEmails(pendingEmails);
      if (emailDraft) {
        setEmailDraft(emailDraft);
        setShowDraftEditor(true);
      } else {
        setEmailDraft((prev) => ({
          ...prev,
          toContacts: [
            {
              name: currentContact.contactName,
              email: currentContact.email || "",
            },
          ],
          ccContacts: [],
          subject: gmailThread?.messages[0]?.subject
            ? `Re: ${gmailThread?.messages[0]?.subject}`
            : "",
          body: prev?.body?.length > 0 ? prev.body : "",
        }));
      }
    } catch (error) {
      if (error?.message?.includes("Google integration")) {
        setMissingIntegration(true);
      } else {
        setErrorAlert(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const noSubject = !gmailThread?.messages[0]?.subject;

  const handleSendDraftEmail = async (scheduleAt?: null | string) => {
    if (!emailHealth) {
      setOpenIntegrationDialog(true);
      return;
    }

    if (hasInvalidEmail(emailDraft)) {
      setAlert(
        "Some of the entered email addresses were invalid. Please make sure they are properly formatted.",
        "error",
      );
      return;
    }

    try {
      setSendLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-contacts/${outreachContactId}/send-gmail-message`,
        "POST",
        {},
        {
          ...emailDraft,
          scheduleAt,
          emailDraftId: emailDraft.id,
          gmailThreadId: emailDraft?.gmailThreadId || gmailThread?.id,
          disableFollowUpTasks: emailThreadSetting.disableFollowUpTasks,
          sendFrom: getPageName(),
        },
      );
      if (scheduleAt) {
        setAlert("Successfully scheduled email!", "success");
      } else {
        setAlert("Successfully sent email!", "success");
      }

      if (res.completedActionItemIds) {
        updateActionItems(res.completedActionItemIds);
      }
      setShowDraftEditor(false);
      setEmailDraft({
        toContacts: [],
        ccContacts: [],
        body: "",
        subject: "",
      });
      fetchEmailThread();
      fetchEmailThreads(outreachContactId, true);
      fetchContact(outreachContactId);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSendLoading(false);
    }
  };

  const filterValidRecipients = (
    contacts?: EmailAddress[],
    exclude?: EmailAddress[],
  ): EmailAddress[] => {
    const excludedEmails = new Set(exclude?.map((c) => c.email));
    return (
      contacts?.filter(
        (c) =>
          !isInvalidEmail(c.email || "") &&
          c.email !== userIntegration?.email &&
          !excludedEmails.has(c.email),
      ) || []
    );
  };

  const getReplyRecipients = (
    messages?: Message[],
  ): { to: EmailAddress[]; cc: EmailAddress[] } | null => {
    if (!messages || messages.length === 0) {
      return null;
    }

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.is_sent_by_user) {
      const to = filterValidRecipients(lastMessage.to);
      return {
        to,
        cc: filterValidRecipients(lastMessage.cc, to),
      };
    }

    const to = [lastMessage.from_];
    return {
      to,
      cc: filterValidRecipients([...lastMessage.to, ...lastMessage.cc], to),
    };
  };

  const reply = () => {
    if (!currentContact) {
      return;
    }

    const recipients = getReplyRecipients(gmailThread?.messages);
    if (!recipients) {
      return;
    }

    trackEvent("Outreach Contact Thread Reply Button Clicked", {
      contactId: outreachContactId,
      "Gmail Thread ID": gmailThread?.id,
      emailDraft,
    });

    setEmailDraft({
      toContacts: recipients.to,
      ccContacts: [],
      body: "",
      subject: gmailThread?.messages[0]?.subject
        ? `Re: ${gmailThread?.messages[0]?.subject}`
        : "",
    });
    setShowDraftEditor(true);
  };

  const replyAll = () => {
    if (!currentContact) {
      return;
    }

    const recipients = getReplyRecipients(gmailThread?.messages);
    if (!recipients) {
      return;
    }

    trackEvent("Outreach Contact Thread Reply All Button Clicked", {
      contactId: outreachContactId,
      "Gmail Thread ID": gmailThread?.id,
      emailDraft,
    });

    setEmailDraft({
      toContacts: recipients.to,
      ccContacts: recipients.cc,
      body: "",
      subject: gmailThread?.messages[0]?.subject
        ? `Re: ${gmailThread?.messages[0]?.subject}`
        : "",
    });
    setShowDraftEditor(true);
  };

  useEffect(() => {
    fetchEmailThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContact?.id, threadId, currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  if (!currentContact) {
    return <></>;
  }

  const noThreadFound = (
    <Typography
      component="div"
      sx={{ mt: 2, mx: "auto", textAlign: "center" }}
      variant="caption"
      color="textSecondary"
    >
      No email thread could be found. Contact hello@onbento.com for assistance
      if this is not expected.
    </Typography>
  );

  const hasPendingEmail = pendingEmails.length > 0;

  const getDialogContent = () => (
    <>
      {isSmallScreen && (
        <DialogTitle sx={styles.dialogTitleText}>
          <Grid container alignItems="center" justifyContent={"space-between"}>
            <IconButton color="secondary" size="small" onClick={handleBack}>
              <Box component="i" className="fa-solid fa-arrow-left" />
            </IconButton>
            <Box>
              <Typography noWrap sx={styles.brandName}>
                {gmailThread?.messages[0]?.subject}
              </Typography>
            </Box>
            <Box>
              <Typography
                component="span"
                noWrap
                variant="caption"
                color="textSecondary"
              >
                {currentContact?.brand?.name}
              </Typography>
            </Box>
          </Grid>
        </DialogTitle>
      )}
      <DialogContent sx={styles.dialogContent}>
        {loading && (
          <Grid
            container
            sx={styles.progressContainer}
            gap={2}
            alignItems="center"
            justifyContent="center"
          >
            <Skeleton
              animation="wave"
              variant="rounded"
              height={40}
              width="90%"
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              height={30}
              width="90%"
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              height={400}
              width="90%"
            />
          </Grid>
        )}

        {missingIntegration && (
          <Alert severity="error">
            Log in to Google on your{" "}
            <Link style={{ color: "black" }} to={`/${routes.settings}`}>
              settings page
            </Link>{" "}
            to continue using this feature
          </Alert>
        )}

        {gmailThread && currentUser && (
          <>
            {gmailThread?.messages?.length > 0 ? (
              <EmailThread
                messages={gmailThread.messages}
                openAttachment={openAttachment}
                showSubject={!isSmallScreen}
              />
            ) : (
              <>
                {currentContact &&
                  currentContact.automatedStatus ===
                    OutreachContactAutomatedStatus.invalid &&
                  noThreadFound}
              </>
            )}

            {!hasPendingEmail && (
              <>
                {(showDraftEditor || displayFollowUp) && (
                  <EmailDraftComponent
                    emailDraft={emailDraft}
                    gmailThread={gmailThread}
                    setEmailDraft={setEmailDraft}
                    handleSendDraftEmail={handleSendDraftEmail}
                    from={{
                      email: userIntegration?.email || currentUser.email,
                      name: currentUser.name,
                    }}
                    cancelDraft={() => {
                      setShowDraftEditor(false);
                      searchParams.delete("display_followup_templates");
                      setSearchParams(searchParams);
                    }}
                    sendLoading={sendLoading}
                    displaySubjectEditor={
                      noSubject && gmailThread.messages?.length === 0
                    }
                    emailThreadSetting={emailThreadSetting}
                    setEmailThreadSetting={setEmailThreadSetting}
                    currentContact={currentContact}
                    displayFollowUp={displayFollowUp}
                  />
                )}
                {!(showDraftEditor || displayFollowUp) && (
                  <Grid
                    container
                    spacing={1}
                    sx={{ mt: 1 }}
                    justifyContent="center"
                  >
                    <Grid item xs="auto">
                      <Button onClick={reply} variant="outlined">
                        Reply{" "}
                        <Box
                          component="i"
                          className="fa-solid fa-reply"
                          sx={{ ml: 1 }}
                        />
                      </Button>
                    </Grid>
                    <Grid item xs="auto">
                      <Button onClick={replyAll} variant="outlined">
                        Reply All{" "}
                        <Box
                          component="i"
                          className="fa-solid fa-reply-all"
                          sx={{ ml: 1 }}
                        />
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {hasPendingEmail && (
              <PendingEmailsDraft
                pendingEmails={pendingEmails}
                setPendingEmails={setPendingEmails}
                setGmailThread={setGmailThread}
                gmailThread={gmailThread}
                setShowDraftEditor={setShowDraftEditor}
              />
            )}
          </>
        )}
      </DialogContent>
    </>
  );

  if (!isSmallScreen) {
    return getDialogContent();
  }

  return (
    <Drawer open={true} onClose={closeDrawer}>
      {getDialogContent()}
      <ConfirmDialog
        header={`Are you sure?`}
        subheader={
          "You have an unsaved draft. Are you sure you want to navigate away from this page?"
        }
        open={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        handleConfirm={() => navigate(-1)}
      />
    </Drawer>
  );
}
